import type {
  AssemblyInstructionSuggestion,
  CompatibilityResult,
  DescriptionLang,
  SalesDescriptionSuggestion,
} from '@klingerator/shared';
import { api } from './api';

export interface TemplateCandidate {
  template_id: number;
  name: string;
  department: string | null;
  variant: 'm' | 'tr' | null;
  donor_code: string;
  confidence: number;
  why: string;
}

/** AI-hulpfuncties los van de import-wizard. */
export const aiService = {
  /** Vertaalt een cryptische Unit4-foutmelding naar oorzaak + oplossing in het Nederlands. */
  async explainUnit4Error(payload: {
    error: string;
    operation?: string;
    endpoint?: string;
    productName?: string;
    productCode?: string;
    templateCode?: string;
    unitCode?: string;
    env?: string;
  }): Promise<string> {
    const { data } = await api.post<{ explanation: string }>('/ai/explain-unit4-error', payload, {
      silent: true,
    });
    return data.explanation;
  },

  /**
   * Suggestie voor een omschrijving binnen het Unit4-limiet van 70 tekens (te lang ⇒ inkorten,
   * anders verbeteren); met `productId` gebruikt de AI de regels als context.
   */
  async shortenDescription(text: string, productId?: number | null): Promise<string> {
    const { data } = await api.post<{ suggestion: string }>('/ai/shorten-description', {
      text,
      product_id: productId ?? undefined,
    });
    return data.suggestion;
  },
  /** AI-voorstel voor de product-/verkoopomschrijving (per sjabloonsectie) op basis van de regels. */
  async suggestSalesDescription(
    productId: number,
    lang: DescriptionLang,
  ): Promise<{ suggestion: SalesDescriptionSuggestion; model: string }> {
    const { data } = await api.post<{ suggestion: SalesDescriptionSuggestion; model: string }>(
      '/ai/suggest-sales-description',
      { product_id: productId, lang },
      { silent: true, noToast: true },
    );
    return data;
  },
  /** AI-voorstel voor de werkplaatsinstructie (assemblage) op basis van de regels. */
  async suggestAssemblyInstruction(
    productId: number,
  ): Promise<{ suggestion: AssemblyInstructionSuggestion; model: string }> {
    const { data } = await api.post<{ suggestion: AssemblyInstructionSuggestion; model: string }>(
      '/ai/suggest-assembly-instruction',
      { product_id: productId },
      { silent: true, noToast: true },
    );
    return data;
  },
  /** AI-controle of de regels (incl. sub-producten) op elkaar aansluiten (draadmaat, flens, stuurdruk). */
  async checkCompatibility(productId: number): Promise<CompatibilityResult> {
    const { data } = await api.post<CompatibilityResult>(
      '/ai/check-compatibility',
      { product_id: productId },
      { silent: true, noToast: true },
    );
    return data;
  },
  /**
   * AI-voorstel voor het best passende Klingerator-sjabloon (max 3 kandidaten met motivatie): voor een
   * opgeslagen product, of voor een nieuw product op basis van de ingevulde teksten.
   */
  async suggestTemplate(
    target: number | { name: string; description?: string },
  ): Promise<{ candidates: TemplateCandidate[] }> {
    const { data } = await api.post<{ candidates: TemplateCandidate[] }>(
      '/ai/suggest-template',
      typeof target === 'number' ? { product_id: target } : target,
      { silent: true },
    );
    return data;
  },
};
